/**
 * My SoleFinder Profile — three tabs:
 *   1. Free-Text Fit     — describe activity, AI builds your profile
 *   2. Athlete Search    — find shoes worn by a named athlete
 *   3. Direct Search     — keyword search across the catalog
 *
 * All three are powered by Claude Sonnet 4.5 via the universal LLM key.
 * Sign-in required (handled by <ProtectedRoute> wrapper in App.js).
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkle, MagnifyingGlass, PersonSimpleRun, Trophy, ArrowRight,
  CircleNotch, ShoppingBag, Star, Lightning, ChatCircleDots,
} from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'freetext', label: 'Describe my day', icon: <ChatCircleDots size={16} weight="duotone" /> },
  { key: 'athlete',  label: 'Find an athlete', icon: <Trophy size={16} weight="duotone" /> },
  { key: 'search',   label: 'Search a shoe',   icon: <MagnifyingGlass size={16} weight="duotone" /> },
];

const FREETEXT_PROMPTS = [
  'I play indoor 5-a-side twice a week and run 5km on weekends',
  'I work on my feet 10 hours a day, retail floor, mostly standing',
  'I want a casual everyday shoe for university, prefer black or white',
  'I hike 8km trails on weekends and need ankle support',
];

const ATHLETE_PROMPTS = [
  'Patrick Mahomes', 'Cristiano Ronaldo', 'LeBron James', 'Eliud Kipchoge',
];

export default function ProfilePage() {
  const [tab, setTab] = useState('freetext');
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-10 pb-24">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> Your SoleFinder Profile
          </p>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase">
            Built for {user?.name || 'you'}.
          </h1>
          <p className="text-[#52525B] mt-3 max-w-xl">
            Three smart shortcuts powered by AI. Describe your day in plain English,
            ask which shoes a pro athlete wears, or search the catalog directly.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex gap-2 flex-wrap" data-testid="profile-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`profile-tab-${t.key}`}
              data-state={tab === t.key ? 'on' : 'off'}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide border-2 transition-all
                ${tab === t.key
                  ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white'
                  : 'border-[#E4E4E7] bg-white text-[#0A0A0A] hover:border-[#FF3B30] hover:text-[#FF3B30]'}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          {tab === 'freetext' && <FreeTextTab />}
          {tab === 'athlete' && <AthleteTab />}
          {tab === 'search' && <SearchTab />}
        </motion.div>
      </div>
    </div>
  );
}

// ============== TAB 1: Free-text ==============
function FreeTextTab() {
  const [desc, setDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  const { format } = useCurrency();

  const submit = async () => {
    setError(''); setResult(null);
    if (desc.trim().length < 6) {
      setError('Tell us a bit more about what you do.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/profile/free-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ description: desc }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not analyse your description');
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const viewFullResults = () => {
    if (!result) return;
    navigate(`/results/${result.profile_id}`, { state: { results: result } });
  };

  return (
    <div data-testid="freetext-tab">
      <p className="text-sm text-[#52525B] mb-3">
        Describe your day, your activities, anything specific about your feet. The AI will build a full fit profile for you.
      </p>
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="e.g. I play indoor 5-a-side twice a week and run 5km on weekends. I have flat feet."
        rows={4}
        maxLength={800}
        disabled={loading}
        className="w-full border-2 border-[#E4E4E7] p-4 text-base focus:border-[#FF3B30] focus:outline-none transition-colors disabled:opacity-50"
        data-testid="freetext-input"
      />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="flex flex-wrap gap-1.5">
          {FREETEXT_PROMPTS.map(p => (
            <button key={p} onClick={() => setDesc(p)} disabled={loading}
              className="text-[10px] font-bold uppercase tracking-wide bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
              {p.slice(0, 38)}{p.length > 38 ? '…' : ''}
            </button>
          ))}
        </div>
        <button
          onClick={submit}
          disabled={loading || desc.trim().length < 6}
          className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#D62828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="freetext-submit">
          {loading
            ? <><CircleNotch size={16} className="animate-spin" /> Analysing</>
            : <><Lightning size={16} weight="fill" /> Build my profile</>}
        </button>
      </div>
      {error && <p className="mt-3 text-sm text-[#FF3B30]" data-testid="freetext-error">{error}</p>}

      {result && (
        <div className="mt-8" data-testid="freetext-result">
          <div className="bg-[#0A0A0A] text-white p-5 mb-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">AI interpretation</p>
            <p className="text-sm leading-relaxed">{result.interpretation?.summary || 'Profile built.'}</p>
            <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
              {result.interpretation?.activity && <span className="bg-white/10 px-2 py-0.5 font-bold uppercase">{result.interpretation.activity}</span>}
              {result.interpretation?.arch_type && <span className="bg-white/10 px-2 py-0.5 font-bold uppercase">{result.interpretation.arch_type} arch</span>}
              {result.interpretation?.foot_width && <span className="bg-white/10 px-2 py-0.5 font-bold uppercase">Width {result.interpretation.foot_width}</span>}
              {result.interpretation?.budget && <span className="bg-white/10 px-2 py-0.5 font-bold uppercase">${result.interpretation.budget}</span>}
            </div>
          </div>
          <PickGrid recs={result.recommendations || []} format={format} />
          {result.recommendations?.length > 0 && (
            <button onClick={viewFullResults}
              className="mt-4 inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
              data-testid="freetext-view-all">
              See full results page <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============== TAB 2: Athlete ==============
function AthleteTab() {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const { format } = useCurrency();

  const submit = async (preset) => {
    const q = (preset ?? name).trim();
    if (preset) setName(preset);
    setError(''); setResult(null);
    if (q.length < 2) { setError('Enter an athlete name.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/profile/athlete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ athlete: q }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not find that athlete');
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="athlete-tab">
      <p className="text-sm text-[#52525B] mb-3">
        Type any pro athlete's name. The AI will look up the shoes they wear and surface matches from our catalog.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. Patrick Mahomes"
          disabled={loading}
          maxLength={80}
          className="flex-1 border-2 border-[#E4E4E7] px-4 py-3 text-base focus:border-[#FF3B30] focus:outline-none transition-colors disabled:opacity-50"
          data-testid="athlete-input"
        />
        <button onClick={() => submit()} disabled={loading || name.trim().length < 2}
          className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#D62828] transition-colors disabled:opacity-50"
          data-testid="athlete-submit">
          {loading
            ? <CircleNotch size={16} className="animate-spin" />
            : <PersonSimpleRun size={16} weight="duotone" />}
          Find
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {ATHLETE_PROMPTS.map(p => (
          <button key={p} onClick={() => submit(p)} disabled={loading}
            className="text-[10px] font-bold uppercase tracking-wide bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
            {p}
          </button>
        ))}
      </div>
      {error && <p className="mt-3 text-sm text-[#FF3B30]" data-testid="athlete-error">{error}</p>}

      {result && (
        <div className="mt-8" data-testid="athlete-result">
          {result.note && (
            <div className="bg-[#0A0A0A] text-white p-5 mb-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
                {result.athlete} · {result.sport}
              </p>
              <p className="text-sm">{result.note}</p>
              {result.suggested_shoes?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
                  {result.suggested_shoes.map((s, i) => (
                    <span key={i} className="bg-white/10 px-2 py-0.5 font-bold uppercase">
                      {s.brand} {s.model}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {result.catalog_matches?.length > 0 ? (
            <PickGrid recs={result.catalog_matches} format={format} />
          ) : (
            <p className="text-[#A1A1AA] text-sm" data-testid="athlete-no-matches">
              We couldn't find those specific shoes in our catalog yet. Try the search tab or describe your activity.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============== TAB 3: Direct search ==============
function SearchTab() {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const { format } = useCurrency();

  const submit = useCallback(async () => {
    setError(''); setResult(null);
    if (q.trim().length < 2) { setError('Enter at least 2 characters.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/profile/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ query: q.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Search failed');
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Debounced live search
  useEffect(() => {
    if (q.trim().length < 2) { setResult(null); return; }
    const t = setTimeout(() => submit(), 400);
    return () => clearTimeout(t);
  }, [q, submit]);

  return (
    <div data-testid="search-tab">
      <p className="text-sm text-[#52525B] mb-3">
        Looking for a specific shoe? Search by brand, model, or category.
      </p>
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. Vaporfly, Air Jordan, Hoka Bondi"
          autoFocus
          maxLength={80}
          className="w-full border-2 border-[#E4E4E7] pl-12 pr-4 py-3 text-base focus:border-[#FF3B30] focus:outline-none transition-colors"
          data-testid="search-input"
        />
        {loading && (
          <CircleNotch size={18} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[#FF3B30]" />
        )}
      </div>
      {error && <p className="mt-3 text-sm text-[#FF3B30]" data-testid="search-error">{error}</p>}

      {result && (
        <div className="mt-8" data-testid="search-result">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mb-3">
            {result.count} match{result.count === 1 ? '' : 'es'} for "{result.query}"
          </p>
          {result.results.length === 0 ? (
            <p className="text-[#A1A1AA] text-sm" data-testid="search-empty">
              No shoes matched. Try a brand or category like "running" or "hoka".
            </p>
          ) : (
            <PickGrid recs={result.results} format={format} />
          )}
        </div>
      )}
    </div>
  );
}

// ============== Shared result grid ==============
function PickGrid({ recs, format }) {
  if (!recs?.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-pick-grid">
      {recs.map(shoe => {
        const prices = shoe.store_prices || [];
        const lowest = prices.length
          ? prices.reduce((a, b) => ((+a.price || 99999) < (+b.price || 99999) ? a : b))
          : null;
        return (
          <Link
            key={shoe.shoe_id}
            to={`/shoe/${shoe.shoe_id}`}
            className="block border-2 border-[#E4E4E7] hover:border-[#FF3B30] transition-colors group"
            data-testid={`profile-pick-${shoe.shoe_id}`}>
            <div className="aspect-square bg-[#FAFAFA] overflow-hidden">
              <img src={shoe.image_url} alt={shoe.model}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
            </div>
            <div className="p-3">
              <p className="text-[10px] text-[#A1A1AA] font-bold uppercase tracking-wide">{shoe.brand}</p>
              <p className="text-sm font-bold leading-tight truncate">{shoe.model}</p>
              {shoe.match_score && (
                <p className="text-[11px] text-[#FF3B30] font-bold mt-1">{shoe.match_score}% match</p>
              )}
              {lowest && (
                <p className="mt-1.5 text-base font-black text-[#0A0A0A]">
                  {format(lowest.price, lowest.currency)}
                  <span className="text-[10px] text-[#A1A1AA] font-bold ml-1">at {lowest.store_name}</span>
                </p>
              )}
              {shoe.athlete_context && (
                <p className="text-[10px] text-[#A1A1AA] mt-1 italic">{shoe.athlete_context}</p>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
